import { useEffect, useState } from 'react'

/**
 * A clock that ticks ONLY while it is needed.
 *
 * Why this exists: the Queue page used to hold one page-level `now` ticking at
 * 1 Hz unconditionally. That re-rendered the whole page every second, and two
 * things broke downstream —
 *   1. `QueueTable`'s `columns` useMemo depended on `now`, so the column array
 *      got a new identity every second, TanStack rebuilt the cell renderers, and
 *      the row `KebabMenu` unmounted/remounted, losing its `open` state (the
 *      "menu closes itself instantly" bug);
 *   2. `Modal`'s focus-trap effect re-ran and pulled focus back to the first
 *      focusable element, which closed any open native `<select>`.
 *
 * So: keep the clock where it is actually consumed (a running row's elapsed
 * time), and stop it when nothing on screen is counting. A static queue does no
 * work at all.
 *
 * @param {boolean} active   tick only while true
 * @param {number}  everyMs  tick interval
 * @returns {number} epoch ms, refreshed on each tick
 */
export function useTicker(active, everyMs = 1000) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!active) return undefined
    // setState lives ONLY in the interval callback — an external subscription,
    // never a synchronous write in the effect body (react-hooks/set-state-in-effect;
    // same shape as the 30s clock in useMyReports). A row that switches from
    // idle to counting can therefore show a value up to one interval old, which
    // at 1s granularity for elapsed time is not observable.
    const t = setInterval(() => setNow(Date.now()), everyMs)
    return () => clearInterval(t)
  }, [active, everyMs])

  return now
}

export default useTicker
